import { html, TemplateResult, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { ClearInitialContentController } from "@/components/meta/clear_initial_content_controller";
import { I18n } from "@/i18n/i18n";
import { tabId } from "@/components/feedback/feedback_tab";
import "@/components/feedback/feedback_tab";
import "@/components/feedback/feedback_code_tab";
import { renderMessages } from "@/feedback/message_helpers";
import { calloutTemplate } from "@/callout";
import codeListing from "@/code_listing";
import type {
    Tab,
    MachineAnnotation,
    FeedbackResult,
    FeedbackEnvelope,
    FeedbackEnvelopeContext,
} from "@/types/FeedbackResult";
import { sessionStore } from "@/storage";
import { scrollPaddingTop } from "@/scroll_pin";
import { countGroups, type TestCounts } from "@/feedback/test_counts";

/** The detail of `feedback-ready`. */
export type FeedbackReadyDetail = { submissionId: number; counts: TestCounts };

type FeedbackPane =
    | { kind: "feedback"; tab: Tab; tabIndex: number; id: string; triggerId: string }
    | { kind: "code"; annotations: MachineAnnotation[]; id: "code-tab"; triggerId: "link-to-code-tab" };

/**
 * Root component for the client-side feedback table.
 * Receives the submission result via the `envelope` property (set by an inline
 * script in the Rails view) and renders a tab bar with one `d-feedback-tab`
 * per top-level group.
 *
 * @element d-feedback-table
 *
 * @prop {number}           submission-id - ID of the submission (used for sessionStorage keys).
 * @prop {FeedbackEnvelope} envelope      - Set by the inline Rails script; drives all rendering.
 *
 * @fires feedback-ready - Bubbles once, after the first render with an envelope; detail: { submissionId: number, counts: TestCounts }
 */
@customElement("d-feedback-table")
export class FeedbackTable extends DodonaElement {
    clearInitialContent = new ClearInitialContentController(this);

    @property({ type: Number, attribute: "submission-id" }) submissionId: number | null = null;
    @property({ type: Object }) envelope: FeedbackEnvelope | null = null;
    @state() activeTabIndex: number = 0;
    @state() private panes: FeedbackPane[] = [];
    @state() private _codeTabReady = false;
    private _tabsInitialized = false;
    private _panesForResult: FeedbackResult | null = null;
    private _result: FeedbackResult | null = null;
    private _code: string | null = null;
    private _programmingLanguage: string | null = null;
    private _submissionContext: FeedbackEnvelopeContext = {};
    private _loaded = false;
    private _readyReported = false;

    override willUpdate(changedProperties: PropertyValues): void {
        super.willUpdate(changedProperties);

        if (changedProperties.has("submissionId")) {
            this._tabsInitialized = false;
            this._panesForResult = null;
            this._codeTabReady = false;
        }

        if (changedProperties.has("envelope")) {
            if (this.envelope !== null) {
                this._result = this.envelope.result ?? null;
                this._code = this.envelope.code ?? null;
                this._programmingLanguage = this.envelope.programmingLanguage ?? null;
                this._submissionContext = this.envelope.context ?? {};
                this._loaded = true;
                // Build the code tab only after the active tab has painted, so
                // its (potentially very large) code listing never blocks first
                // render. Two rAFs: the first lets the active tab paint, the
                // second lets Lit flush before we mount the code listing.
                // Capture the submission so a callback queued for a previous
                // submission can't flip the flag after we re-defer on navigation.
                const submissionId = this.submissionId;
                requestAnimationFrame(() =>
                    requestAnimationFrame(() => {
                        if (this.submissionId === submissionId) {
                            this._codeTabReady = true;
                        }
                    }),
                );
            }
        }

        const result = this._result;
        if (result && this._panesForResult !== result) {
            this.panes = this._buildPanes(result);
            this._panesForResult = result;
        }

        if (!this._tabsInitialized && this._loaded && result) {
            this._tabsInitialized = true;
            this.activeTabIndex = this._resolveInitialPane(this.panes);
        }
    }

    override updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);
        if (this._loaded && !this._readyReported) {
            this._readyReported = true;
            this.dispatchEvent(
                new CustomEvent<FeedbackReadyDetail>("feedback-ready", {
                    bubbles: true,
                    detail: { submissionId: this.submissionId, counts: this.testCounts },
                }),
            );
        }
    }

    /** The correct and total groups over all feedback tabs. */
    private get testCounts(): TestCounts {
        return countGroups(this.panes.flatMap((pane) => (pane.kind === "feedback" ? (pane.tab.groups ?? []) : [])));
    }

    private _buildPanes(result: FeedbackResult): FeedbackPane[] {
        const tabs = result.groups ?? [];
        const panes: FeedbackPane[] = [];

        for (let tabIndex = 0; tabIndex < tabs.length; tabIndex++) {
            const tab = tabs[tabIndex];
            // Results of the Python judge from before May 2023 have an extra tab without tests
            // that holds its lint comments in `data.source_annotations`.
            if (tab.data?.source_annotations !== undefined) continue;
            const id = tabId(tabIndex);
            panes.push({ kind: "feedback", tab, tabIndex, id, triggerId: `${id}-trigger` });
        }

        panes.push({
            kind: "code",
            annotations: (result.annotations ?? []) as MachineAnnotation[],
            id: "code-tab",
            triggerId: "link-to-code-tab",
        });

        return panes;
    }

    private _resolveInitialPane(panes: FeedbackPane[]): number {
        // 1. Try URL hash
        const hash = location.hash.replace(/^#/, "");
        if (hash) {
            // "code" is the legacy deep-link anchor (anchor: 'code') used across the app.
            const idx = panes.findIndex((pane) => pane.id === hash || (hash === "code" && pane.kind === "code"));
            if (idx >= 0) return idx;
        }

        // 2. Try sessionStorage
        if (this.submissionId !== null) {
            const stored = sessionStore.get(`dodona-active-tab-${this.submissionId}`);
            if (stored !== null) {
                const parsed = parseInt(stored, 10);
                if (!isNaN(parsed) && parsed >= 0 && parsed < panes.length) {
                    return parsed;
                }
            }
        }

        return panes.findIndex((pane) => pane.kind === "feedback") >= 0 ? 0 : Math.max(0, panes.length - 1);
    }

    /**
     * Activates pane `i`. The returned promise settles once the switch is on screen,
     * including the jump to the pane's top when the tab bar is stuck.
     */
    private _selectTab(i: number): Promise<void> {
        // Measured before the panes swap: the outgoing pane collapses and the incoming
        // one is not laid out yet, so the geometry after the update no longer reflects
        // where the user was.
        const cardTab = this.querySelector(".card-tab");
        const content = this.querySelector<HTMLElement>(".tab-content");
        const barStuck = cardTab !== null && cardTab.getBoundingClientRect().top < scrollPaddingTop();
        if (barStuck && content) {
            // Hold the height across the swap: a shorter intermediate page would clamp
            // the scroll position before the new pane exists.
            content.style.minHeight = `${content.offsetHeight}px`;
        }

        this.activeTabIndex = i;
        history.replaceState(null, "", `#${this.panes[i].id}`);
        if (this.submissionId !== null) {
            sessionStore.set(`dodona-active-tab-${this.submissionId}`, String(i));
        }
        // The sticky tab bar is always on screen; what matters is whether it is stuck,
        // i.e. the user scrolled past its natural position and is somewhere inside a
        // tab's content. Only then does the new tab need to be brought to its top;
        // from the natural position a scroll would just move the page under the cursor.
        // The pane's nested components render after this update, so wait a frame.
        if (!barStuck) return this.updateComplete.then(() => undefined);
        return this.updateComplete.then(
            () =>
                new Promise((resolve) =>
                    requestAnimationFrame(() => {
                        this._showActivePaneTop();
                        resolve();
                    }),
                ),
        );
    }

    /**
     * Jumps so the tab bar sits on its sticky line with the active pane below it, as
     * in the page's initial layout, and releases the height hold from `_selectTab`.
     * A pane shorter than the viewport cannot reach that position, so the enclosing
     * card gets bottom margin for the missing distance, recomputed on every call.
     */
    private _showActivePaneTop(): void {
        const cardTab = this.querySelector<HTMLElement>(".card-tab");
        const content = this.querySelector<HTMLElement>(".tab-content");
        const pane = this.querySelector<HTMLElement>(".tab-pane.active");
        const bar = this.querySelector<HTMLElement>(".card-tab .nav-tabs");
        if (!cardTab || !content || !pane || !bar) return;
        // With the wrapper's top on the sticky line the bar's natural position is the
        // position it is stuck in, so the bar does not move and the pane keeps the
        // bar's bottom margin above it. Any lower and the bar moves down; any higher
        // and the bar eats into that margin.
        const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
        const target = window.scrollY + cardTab.getBoundingClientRect().top - stickyTop;
        const card = this.closest<HTMLElement>(".card");
        if (card) {
            card.style.marginBottom = "";
            // The page height once the hold is released: the held content shrinks to the pane.
            const pageHeight = document.documentElement.scrollHeight - content.offsetHeight + pane.offsetHeight;
            const missing = target + window.innerHeight - pageHeight;
            if (missing > 0) {
                const margin = parseFloat(getComputedStyle(card).marginBottom) || 0;
                card.style.marginBottom = `${margin + missing}px`;
            }
        }
        content.style.minHeight = "";
        window.scrollTo({ top: target, behavior: "instant" });
    }

    /**
     * Handles clicks on judge-authored `<a class="tab-link">` anchors.
     * Always switches to the code tab (judges only generate code-tab links)
     * and scrolls to and highlights the line referenced by `data-line`.
     */
    private _onTabLinkClick(e: Event): void {
        const link = (e.target as Element).closest("a.tab-link") as HTMLAnchorElement | null;
        if (!link) return;
        e.preventDefault();
        const codeTabIdx = this.panes.findIndex((p) => p.kind === "code");
        const switched = codeTabIdx >= 0 ? this._selectTab(codeTabIdx) : this.updateComplete;
        const line = parseInt(link.dataset.line ?? "", 10);
        if (!isNaN(line)) {
            // After the switch, so the line scroll is the last one and wins.
            void switched.then(() => {
                if (!document.querySelector(`tr.lineno#line-${line}`)) return;
                codeListing.clearHighlights();
                codeListing.highlightLine(line, true);
            });
        }
    }

    /**
     * Arrow keys move to the next or previous tab, Home and End to the first or
     * last. The tab list holds a single tab stop, so focus has to follow the
     * selection instead of the browser moving it.
     */
    private _onTabKeydown(e: KeyboardEvent): void {
        // Alt+Left and Alt+Right are browser history, and the other modifiers
        // carry their own shortcuts.
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

        const last = this.panes.length - 1;
        let next: number;

        switch (e.key) {
            case "ArrowRight":
                next = this.activeTabIndex === last ? 0 : this.activeTabIndex + 1;
                break;
            case "ArrowLeft":
                next = this.activeTabIndex === 0 ? last : this.activeTabIndex - 1;
                break;
            case "Home":
                next = 0;
                break;
            case "End":
                next = last;
                break;
            default:
                return;
        }

        e.preventDefault();
        void this._selectTab(next);
        void this.updateComplete.then(() => {
            this.querySelector<HTMLElement>(`#${this.panes[next].triggerId}`)?.focus();
        });
    }

    private _renderTabHeader(pane: FeedbackPane, i: number): TemplateResult {
        const isActive = i === this.activeTabIndex;

        if (pane.kind === "code") {
            return html`<button
                type="button"
                class="${isActive ? "active" : ""}"
                id="link-to-code-tab"
                role="tab"
                aria-controls="code-tab"
                aria-selected="${isActive}"
                tabindex="${isActive ? 0 : -1}"
                @click=${() => this._selectTab(i)}
            >
                ${I18n.t("js.submissions_show.code")} <d-annotations-count-badge></d-annotations-count-badge>
            </button>`;
        }

        const tab = pane.tab;
        const permission = tab.permission ?? "student";
        let tooltip = "";
        if (permission === "zeus") {
            tooltip = I18n.t("js.submissions_show.tab_zeus");
        } else if (permission === "educator") {
            tooltip = I18n.t("js.submissions_show.tab_educator");
        }
        const badgeCount = tab.badgeCount != null && tab.badgeCount > 0 ? String(tab.badgeCount) : "";

        return html`<button
            type="button"
            class="tab-${permission} ${isActive ? "active" : ""}"
            id="${pane.triggerId}"
            role="tab"
            aria-controls="${pane.id}"
            aria-selected="${isActive}"
            tabindex="${isActive ? 0 : -1}"
            title="${tooltip}"
            @click=${() => this._selectTab(i)}
        >
            ${tab.description ? tab.description.charAt(0).toUpperCase() + tab.description.slice(1) : ""}
            <span class="badge rounded-pill" id="badge_${pane.id}">${badgeCount}</span>
        </button>`;
    }

    private _renderPane(pane: FeedbackPane, i: number): TemplateResult {
        const active = i === this.activeTabIndex;

        if (pane.kind === "code") {
            return html`<div
                class="tab-pane feedback-tab-pane ${active ? "active" : ""}"
                id="code-tab"
                role="tabpanel"
                aria-labelledby="link-to-code-tab"
                tabindex="${active ? "0" : "-1"}"
                aria-hidden="${active ? "false" : "true"}"
            >
                ${
                    this._codeTabReady
                        ? html`<d-feedback-code-tab
                              .annotations=${pane.annotations}
                              .code=${this._code}
                              .programmingLanguage=${this._programmingLanguage}
                              .submissionContext=${this._submissionContext}
                          ></d-feedback-code-tab>`
                        : html`<div class="feedback-tab-loading">
                              <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i>
                              ${I18n.t("js.submissions_show.loading")}
                          </div>`
                }
            </div>`;
        }

        return html`<d-feedback-tab
            .tab=${pane.tab}
            .tabIdx=${pane.tabIndex}
            .active=${active}
            .code=${this._code}
            .submissionContext=${this._submissionContext}
        ></d-feedback-tab>`;
    }

    render(): TemplateResult {
        if (!this._loaded) {
            return html`<div class="feedback-table-loading">
                <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i>
                ${I18n.t("js.submissions_show.loading")}
            </div>`;
        }

        const result = this._result;
        if (!result) {
            return html`<div class="feedback-table">
                ${calloutTemplate({
                    type: "danger",
                    icon: "alert-circle-outline",
                    message: I18n.t("js.submissions_show.reading_failed"),
                    role: "alert",
                })}
            </div>`;
        }

        const context = this._submissionContext;

        return html`
            <div class="feedback-table" data-exercise_id="${context.exerciseId ?? ""}" @click=${this._onTabLinkClick}>
                ${
                    result.messages?.length
                        ? html`<div class="feedback-table-messages">${renderMessages(result.messages, context)}</div>`
                        : ""
                }
                <div class="card-tab">
                    <div
                        class="nav nav-tabs sticky"
                        role="tablist"
                        aria-label="${I18n.t("js.submissions_show.feedback_tabs")}"
                        @keydown=${this._onTabKeydown}
                    >
                        ${this.panes.map((pane, i) => this._renderTabHeader(pane, i))}
                    </div>
                    <div class="tab-content">${this.panes.map((pane, i) => this._renderPane(pane, i))}</div>
                </div>
            </div>
        `;
    }
}
